// The Firestore contract between this web app and the robot app (ZeenoApp).
// See TESTING_PLAN.md §1.2. Field types mirror ZeenoApp's models/Action.java,
// and COMMANDS mirrors utils/ActionType.java plus the robot's handlers.
//
// This module has no Firebase imports so it can be shared by the web app,
// Node scripts (seed, simulator) and tests. The CommonJS scripts require() it,
// so it must not use top-level await, and any relative import it adds needs a
// file extension (TESTING_PLAN.md Phase 4).

export const COMPANY = 'Zeeno_Dev';

export const logsPath = (sn) => `Companies/${COMPANY}/Actions/${sn}/Logs`;
export const historyPath = (sn) => `Companies/${COMPANY}/ActionsHistory/${sn}/Logs`;
export const pingPath = (sn) => `Companies/${COMPANY}/Actions/${sn}/Ping/pingsetting`;
export const controllerActionsPath = (sn) => `Companies/${COMPANY}/Settings/${sn}/Controller/actions`;

export const TASK_STATUSES = ['upcoming', 'active', 'paused'];

// The robot skips docs missing any of these (ActionViewModel.isValidAction).
export const REQUIRED_FIELDS = ['content', 'status', 'timestamp'];

// Firestore field -> type the robot's doc.toObject(Action.class) expects.
// A wrong type makes toObject throw, which can stop the robot's whole queue.
export const TASK_FIELD_TYPES = {
  by: 'string',
  description: 'string',
  status: 'string',
  timestamp: 'timestamp',
  content: 'stringList',
  robot_name: 'string',
  wifi_status: 'string',
  is_approved: 'boolean',
  is_running: 'boolean',
  battery_amount: 'long', // written by the robot
  next_tasks: 'integer', // written by the robot
};

// content[0] -> names of content[1..]. `optional` counts trailing args that
// may be left out. Commands must be lowercase: the robot dispatches on
// content[0].toUpperCase(), but some checks compare the lowercase name exactly
// (e.g. "video" in ActionViewModel).
export const COMMANDS = {
  goto: { args: ['location'] },
  come_here: { args: ['location'] },
  charging: { args: ['location'] }, // written by the robot only
  escort: { args: ['name', 'from', 'to'] },
  fetch: { args: ['item', 'to', 'from', 'waitSeconds'], optional: 1 }, // note: to before from
  deliver: { args: ['item', 'from', 'to'] },
  video: { args: ['url', 'name', 'length'] }, // length is "m:ss"
  message: { args: ['text'] },
  announcement: { args: ['text'] },
  qr: { args: ['url', 'title'] },
};

const INT32_MIN = -(2 ** 31);
const INT32_MAX = 2 ** 31 - 1;

// Firestore Timestamps from both the web SDK and firebase-admin have
// toMillis(). The web SDK also stores a Date as a Timestamp. A plain
// { seconds, nanoseconds } object would be stored as a map, so it fails.
const isTimestamp = (v) => v instanceof Date || typeof v?.toMillis === 'function';

const TYPE_CHECKS = {
  string: (v) => typeof v === 'string',
  boolean: (v) => typeof v === 'boolean',
  timestamp: isTimestamp,
  long: (v) => Number.isSafeInteger(v),
  integer: (v) => Number.isInteger(v) && v >= INT32_MIN && v <= INT32_MAX,
  stringList: Array.isArray, // items are checked by validateContent
};

const typeName = (v) => (v === null ? 'null' : Array.isArray(v) ? 'array' : typeof v);

function validateContent(content) {
  const errors = [];
  content.forEach((x, i) => {
    if (typeof x !== 'string') errors.push(`content[${i}] must be a string, got ${typeName(x)}`);
  });
  if (errors.length) return errors;

  const [command, ...args] = content;
  if (command === undefined) return ['content must not be empty'];
  const spec = COMMANDS[command];
  if (!spec) return [`content[0] "${command}" is not a known command`];

  const max = spec.args.length;
  const min = max - (spec.optional || 0);
  if (args.length < min || args.length > max) {
    const expected = min === max ? `${max}` : `${min}-${max}`;
    const noun = expected === '1' ? 'argument' : 'arguments';
    errors.push(`${command} takes ${expected} ${noun} [${spec.args.join(', ')}], got ${args.length}`);
  }
  return errors;
}

// Returns a list of reasons the robot would skip or choke on this task doc.
// An empty list means the doc matches the contract.
export function validateTask(doc) {
  if (doc === null || typeof doc !== 'object' || Array.isArray(doc)) {
    return ['task must be an object'];
  }

  const errors = [];
  for (const field of REQUIRED_FIELDS) {
    if (doc[field] === undefined || doc[field] === null) errors.push(`${field} is required`);
  }

  for (const [field, type] of Object.entries(TASK_FIELD_TYPES)) {
    const value = doc[field];
    // Optional fields may be null: toObject leaves the Java field null.
    if (value === undefined || value === null) continue;
    if (!TYPE_CHECKS[type](value)) {
      errors.push(`${field} must be of type ${type}, got ${typeName(value)}`);
    }
  }

  if (typeof doc.status === 'string' && !TASK_STATUSES.includes(doc.status)) {
    errors.push(`status "${doc.status}" must be one of ${TASK_STATUSES.join(', ')}`);
  }
  if (Array.isArray(doc.content)) errors.push(...validateContent(doc.content));

  return errors;
}
